import React, { useState, useEffect, useMemo } from "react";
import clsx from "clsx";
import { Box, Typography } from "@material-ui/core";
import {
  Icon,
  IconName,
  useDeviceTypes,
  TreesCard,
  ExperiencesCrossSell,
  TravelAppBanner,
} from "halifax";
import {
  useCheckoutStateSelector,
  useCheckoutState,
  ContactSelectors,
  cartFulfillSelectors,
  getParentState,
  ParentState,
} from "@capone/checkout";
import { RouteComponentProps } from "react-router-dom";
import {
  ActivitiesEntryTypeEnum,
  APP_GTM_ENTRY,
  ExperiencesSelectionEnum,
} from "redmond";

import "./styles.scss";
import { useIsTravelApp, notifyWalletDataChanged } from "@capone/common";
import {
  ACTIVITY_CONFIRMATION_CODE_TEXT,
  ACTIVITY_CONFIRMATION_CONTACT_PICK_UP,
  ACTIVITY_CONFIRMATION_EARN,
  ACTIVITY_CONFIRMATION_HEADING,
} from "./textConstants";

import { postBookingSurveyPageId } from "./utils";
import { ActivitySummaryPanel } from "../ActivitySummaryPanel";
import {
  AVAILABLE,
  ACTIVITY_CROSS_SELL_EXPERIMENT,
  getExperimentVariant,
  TREES_MODAL_EXPERIMENT,
  useExperiments,
  XMOD_V1_EXPERIMENT,
  getExperimentVariantCustomVariants,
  ACTIVITY_CROSS_SELL_EXPERIMENT_VARIANTS,
  ACTIVITIES_XSELL,
  STAYS_AND_ACTIVITIES_XSELL,
  TRAVEL_APP_GTM_EXPERIMENT,
} from "../../../../context/experiments";
import {
  ActivityAdditionalDetailsSelectors,
  PickupPointOption,
} from "../../../../checkout";
import { ActivitySelectors } from "../../state/selectors";
import { ConfirmationPageConnectorProps } from "./container";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";

export interface ConfirmationPageProps
  extends RouteComponentProps,
    ConfirmationPageConnectorProps {}

export const ConfirmationPage = ({
  largestValueAccount,
  rewardsKey,
  fromDate,
  untilDate,
  history,
}: ConfirmationPageProps) => {
  const { matchesMobile } = useDeviceTypes();
  const [state] = useCheckoutState();
  const parentState = getParentState(state.value) as ParentState;

  const [isProdEnv, setIsProdEnv] = useState(
    () =>
      typeof window !== "undefined" &&
      window.__mclean_env__ &&
      window.__mclean_env__.ENV === "production",
  );

  const { isTravelApp } = useIsTravelApp();

  useEffect(() => {
    // Only update if initial check wasn't successful
    if (!isProdEnv && typeof window !== "undefined" && window.__mclean_env__) {
      const isProduction = window.__mclean_env__.ENV === "production";
      setIsProdEnv(isProduction);

      if (!isProduction) {
        console.warn("Using test survey ID for activities confirmation page.");
      }
    }
  }, [isProdEnv]);

  const expState = useExperiments();
  const isTreesExperiment =
    getExperimentVariant(expState.experiments, TREES_MODAL_EXPERIMENT) ===
    AVAILABLE;
  const activityCrossSellVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    ACTIVITY_CROSS_SELL_EXPERIMENT,
    ACTIVITY_CROSS_SELL_EXPERIMENT_VARIANTS,
  );

  const isActivityCrossSellExperiment =
    activityCrossSellVariant === AVAILABLE ||
    activityCrossSellVariant === ACTIVITIES_XSELL ||
    activityCrossSellVariant === STAYS_AND_ACTIVITIES_XSELL;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const earnedRewards = useCheckoutStateSelector(
    cartFulfillSelectors.getFulfillEarnedRewards,
  );
  const emailAddress = useCheckoutStateSelector(
    ContactSelectors.getEmailAddress,
  );
  const confirmationCode = useCheckoutStateSelector(
    cartFulfillSelectors.getFulfilledProductsReservationId,
  );

  const selectedPickupPoint = useCheckoutStateSelector(
    ActivityAdditionalDetailsSelectors.getSelectedPickupPoint,
  );
  const activityShop = useCheckoutStateSelector(
    ActivitySelectors.getActivityShop,
  );
  const travelAppGTMExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_APP_GTM_EXPERIMENT,
  );
  const isTravelAppGTMExperiment = useMemo(
    () => travelAppGTMExperiment === AVAILABLE,
    [travelAppGTMExperiment],
  );

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (parentState === ParentState.bookingConfirmation && confirmationCode) {
      notifyWalletDataChanged("activities confirmation", confirmationCode);
    }
  }, [confirmationCode, parentState]);

  return (
    <Box
      className={clsx("activity-confirmation-root", {
        mobile: matchesMobile,
      })}
    >
      <Box className="activity-confirmation-header">
        <Box className="check-mark-icon">
          <Icon name={IconName.ConfirmationCheckMark} aria-hidden />
        </Box>
        <Typography variant="h2" className="activity-confirmation-heading-text">
          {ACTIVITY_CONFIRMATION_HEADING}
        </Typography>
      </Box>
      <Box className="activity-confirmation-section-container">
        <Typography className="earn-string">
          {ACTIVITY_CONFIRMATION_EARN(earnedRewards, emailAddress)}
        </Typography>
        <Typography className="confirmation-code">
          {ACTIVITY_CONFIRMATION_CODE_TEXT(confirmationCode)}
        </Typography>
      </Box>

      <Box className="activity-confirmation-section-container">
        <ActivitySummaryPanel />
        {selectedPickupPoint === PickupPointOption.PickupLater &&
          activityShop?.supplierInfo && (
            <Box
              className={clsx("activity-confirmation-pickup-later-container", {
                mobile: matchesMobile,
              })}
            >
              <Icon
                className="activity-confirmation-pickup-later-icon"
                name={IconName.Wayfinding}
              />
              <Typography className="activity-confirmation-pickup-later-body">
                {ACTIVITY_CONFIRMATION_CONTACT_PICK_UP()}
              </Typography>
              <Box className="activity-confirmation-pickup-later-supplier-info-container">
                <Box>
                  <Typography>
                    <strong className="supplier-name">
                      {activityShop?.supplierInfo.name}
                    </strong>
                  </Typography>
                </Box>
                <Box>{activityShop?.supplierInfo.phone}</Box>
                <Box>{activityShop?.supplierInfo.email}</Box>
              </Box>
            </Box>
          )}
      </Box>

      {isActivityCrossSellExperiment && (
        <Box className="activity-confirmation-section-container">
          <ExperiencesCrossSell
            largestValueAccount={largestValueAccount}
            rewardsKey={rewardsKey}
            fromDate={fromDate}
            untilDate={untilDate}
            searchLocation={activityShop?.searchLocation}
            experiencesDestination={{
              ExperiencesSelection:
                ExperiencesSelectionEnum.ExperiencesDestination,
              id: activityShop?.shopContext?.destinationId as string,
              name: activityShop?.searchLocation as string,
              searchTerm: activityShop?.searchLocation as string,
            }}
            excludeExperienceId={activityShop?.id.value}
            isXmodRedesign={isXmodRedesign}
            entryType={ActivitiesEntryTypeEnum.ACTIVITIES_XSELL}
            history={history}
            isTravelApp={isTravelApp}
            trackEvent={trackEvent}
            originProduct="activity"
          />
        </Box>
      )}
      <div id={postBookingSurveyPageId(isProdEnv)} />
      {isTravelAppGTMExperiment && !isTravelApp && (
        <TravelAppBanner
          variant="confirmation"
          isMobile={matchesMobile}
          onCTAClick={() => {
            trackEvent({
              eventName: APP_GTM_ENTRY,
              properties: {
                location: "experiences_confirmation",
              },
            });
          }}
        />
      )}
      {isTreesExperiment && <TreesCard isConfirmationScreen />}
    </Box>
  );
};
